/*
 * Kuya announcement-bar rotator (<td-kuya-announcement-bar>).
 *
 * The vendor <announcement-bar> auto-play does not advance on the Kuya
 * waitlist template, so this element drives the rotation of its own
 * <announcement-bar-item> children. It only wraps the bar on that template
 * (see announcement-bar.liquid), where the section intentionally omits the
 * vendor `auto-play` attribute so the two never fight — while keeping
 * `cycle-speed` for this element to read.
 *
 * Rotation matches the vendor model: an item is "shown" when it has no
 * [hidden] attribute. Manual prev/next (vendor) stay compatible because the
 * current item is always read from the DOM.
 */
if (!customElements.get('td-kuya-announcement-bar')) {
  const DEFAULT_SPEED = 8; // seconds — fallback if cycle-speed is absent

  class TdKuyaAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('announcement-bar-item'));
      if (this.items.length < 2) return;

      if (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return;
      }

      // Read cycle-speed from the wrapped <announcement-bar> — the same
      // attribute the vendor auto-play would have used.
      const bar = this.querySelector('announcement-bar');
      const speed = parseInt((bar || this).getAttribute('cycle-speed'), 10);
      this.speed = !speed || speed < 2 ? DEFAULT_SPEED : speed;
      this.timer = null;

      this.advance = this.advance.bind(this);
      this.start = this.start.bind(this);
      this.stop = this.stop.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

      // Normalise to a single visible item, then begin.
      this.show(this.currentIndex());
      this.start();

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.addEventListener('mouseenter', this.stop);
      this.addEventListener('mouseleave', this.start);
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.removeEventListener('mouseenter', this.stop);
      this.removeEventListener('mouseleave', this.start);
    }

    currentIndex() {
      for (let i = 0; i < this.items.length; i++) {
        if (!this.items[i].hasAttribute('hidden')) return i;
      }
      return 0;
    }

    show(index) {
      this.items.forEach((item, i) => {
        if (i === index) {
          item.removeAttribute('hidden');
          if (typeof item.animate === 'function') {
            item.animate(
              { opacity: [0, 1], transform: ['translateY(6px)', 'translateY(0)'] },
              { duration: 200, easing: 'ease' }
            );
          }
        } else {
          item.setAttribute('hidden', '');
        }
      });
    }

    advance() {
      this.show((this.currentIndex() + 1) % this.items.length);
    }

    start() {
      this.stop();
      this.timer = window.setInterval(this.advance, this.speed * 1000);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    // Don't burn cycles while the tab is hidden.
    handleVisibilityChange() {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    }
  }

  customElements.define('td-kuya-announcement-bar', TdKuyaAnnouncementBar);
}
